/**
 * CR158: Cancel Run Dialog
 * Supports normal cancel and force stop for stale RUNNING steps
 */

import { useState, useEffect } from "react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { XCircle, AlertTriangle, Loader2, Zap, Clock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { formatDistanceToNow } from "date-fns";

interface CancelRunDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  batchRunId: string;
  runStatus: string;
  steps: Array<{
    id: string;
    step_key: string;
    step_name: string;
    status: string;
    updated_at?: string;
    started_at?: string | null;
  }>;
  onCancelled: () => void;
}

const STALE_THRESHOLD_MINUTES = 10;

export function CancelRunDialog({
  open,
  onOpenChange,
  batchRunId,
  runStatus,
  steps,
  onCancelled,
}: CancelRunDialogProps) {
  const { toast } = useToast();
  const [cancelType, setCancelType] = useState<"CANCEL" | "FORCE_STOP">("CANCEL");
  const [confirmText, setConfirmText] = useState("");
  const [reason, setReason] = useState("");
  const [isCancelling, setIsCancelling] = useState(false);

  // Find running/enqueued steps
  const activeSteps = steps.filter((s) => 
    s.status === "RUNNING" || s.status === "ENQUEUED"
  );

  // Check for stale steps
  const now = new Date();
  const staleSteps = activeSteps.filter((s) => {
    if (s.status !== "RUNNING") return false;
    const updatedAt = s.updated_at ? new Date(s.updated_at) : null;
    if (!updatedAt) return false;
    const minutesAgo = (now.getTime() - updatedAt.getTime()) / (1000 * 60);
    return minutesAgo >= STALE_THRESHOLD_MINUTES;
  });

  const hasStaleSteps = staleSteps.length > 0;
  const expectedConfirm = cancelType === "FORCE_STOP" ? "FORCE STOP" : "CANCEL";
  const normalizedConfirmText = confirmText.trim().toUpperCase();
  const canConfirm = normalizedConfirmText === expectedConfirm;

  // Button enablement: Cancel tab only requires confirmation, Force Stop requires stale steps
  const canSubmit = cancelType === "CANCEL"
    ? canConfirm && !isCancelling
    : canConfirm && !isCancelling && staleSteps.length > 0;

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setConfirmText("");
      setReason("");
      setCancelType("CANCEL");
    }
  }, [open]);

  const handleCancel = async () => {
    if (!canConfirm || isCancelling) return;

    setIsCancelling(true);
    try {
      const user = await supabase.auth.getUser();
      const userEmail = user.data.user?.email || "unknown";

      // Update all active steps to CANCELLED
      const stepsToCancel = cancelType === "FORCE_STOP" 
        ? staleSteps.map(s => s.id) 
        : activeSteps.map(s => s.id);

      if (stepsToCancel.length > 0) {
        const { error: stepsError } = await supabase
          .from("cr075_batch_run_steps")
          .update({
            status: cancelType === "FORCE_STOP" ? "FAILED" : "CANCELLED",
            finished_at: new Date().toISOString(),
            error_message: cancelType === "FORCE_STOP" 
              ? "Force-stopped (stale RUNNING)" 
              : `Cancelled by ${userEmail}`,
          })
          .in("id", stepsToCancel);

        if (stepsError) throw stepsError;
      }

      // Update batch run status
      const { error: runError } = await supabase
        .from("cr075_batch_runs")
        .update({
          status: "CANCELLED",
          updated_at: new Date().toISOString(),
          config_json: supabase.rpc ? undefined : undefined, // Keep existing config
        })
        .eq("id", batchRunId);

      if (runError) throw runError;

      // Log the cancellation in config_json
      const { data: currentRun } = await supabase
        .from("cr075_batch_runs")
        .select("config_json")
        .eq("id", batchRunId)
        .single();

      if (currentRun) {
        const updatedConfig = {
          ...(currentRun.config_json as Record<string, unknown>),
          cancellation: {
            cancelled_at: new Date().toISOString(),
            cancelled_by: userEmail,
            cancel_type: cancelType,
            reason: reason || undefined,
            steps_affected: stepsToCancel.length,
          },
        };

        await supabase
          .from("cr075_batch_runs")
          .update({ config_json: updatedConfig })
          .eq("id", batchRunId);
      }

      toast({
        title: cancelType === "FORCE_STOP" ? "Run force stopped" : "Run cancelled",
        description: `${stepsToCancel.length} step(s) marked as ${cancelType === "FORCE_STOP" ? "failed" : "cancelled"}`,
      });

      onOpenChange(false);
      onCancelled();
    } catch (err) {
      console.error("Failed to cancel run:", err);
      toast({
        title: "Error",
        description: err instanceof Error ? err.message : "Failed to cancel run",
        variant: "destructive",
      });
    } finally {
      setIsCancelling(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-lg">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <XCircle className="h-5 w-5 text-destructive" />
            Cancel Batch Run
          </AlertDialogTitle>
          <AlertDialogDescription>
            Run ID: <code className="text-xs">{batchRunId.slice(0, 8)}</code>
            <Badge variant="outline" className="ml-2">{runStatus}</Badge>
          </AlertDialogDescription>
        </AlertDialogHeader>

        <Tabs value={cancelType} onValueChange={(v) => setCancelType(v as "CANCEL" | "FORCE_STOP")}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="CANCEL" className="gap-2">
              <XCircle className="h-4 w-4" />
              Cancel
            </TabsTrigger>
            <TabsTrigger 
              value="FORCE_STOP" 
              disabled={!hasStaleSteps} 
              className="gap-2"
            >
              <Zap className="h-4 w-4" />
              Force Stop
              {!hasStaleSteps && (
                <Badge variant="secondary" className="ml-1 text-xs">
                  No stale
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="CANCEL" className="space-y-4 mt-4">
            <Alert>
              <XCircle className="h-4 w-4" />
              <AlertTitle>Cancel Running Steps</AlertTitle>
              <AlertDescription>
                Marks all RUNNING and ENQUEUED steps as CANCELLED. The batch run status 
                will be set to CANCELLED.
              </AlertDescription>
            </Alert>

            {activeSteps.length > 0 && (
              <div className="rounded-lg border p-4 space-y-2">
                <div className="text-sm font-medium">Active steps to cancel:</div>
                <div className="space-y-1">
                  {activeSteps.map((step) => (
                    <div key={step.id} className="flex items-center justify-between text-sm">
                      <span>{step.step_name} ({step.step_key})</span>
                      <Badge variant={step.status === "RUNNING" ? "default" : "secondary"}>
                        {step.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="FORCE_STOP" className="space-y-4 mt-4">
            <Alert variant="destructive">
              <Zap className="h-4 w-4" />
              <AlertTitle>Force Stop Stale Steps</AlertTitle>
              <AlertDescription>
                Marks stale RUNNING steps (no updates in {STALE_THRESHOLD_MINUTES}+ minutes) 
                as FAILED with error message "Force-stopped (stale RUNNING)".
              </AlertDescription>
            </Alert>

            {staleSteps.length > 0 && (
              <div className="rounded-lg border p-4 space-y-2">
                <div className="text-sm font-medium flex items-center gap-2">
                  <Clock className="h-4 w-4 text-orange-500" />
                  Stale steps to force stop:
                </div>
                <div className="space-y-1">
                  {staleSteps.map((step) => {
                    const updatedAt = step.updated_at ? new Date(step.updated_at) : null;
                    return (
                      <div key={step.id} className="flex items-center justify-between text-sm">
                        <span>{step.step_name} ({step.step_key})</span>
                        <span className="text-muted-foreground text-xs">
                          {updatedAt 
                            ? `Updated ${formatDistanceToNow(updatedAt, { addSuffix: true })}`
                            : "No update time"
                          }
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Confirmation required</AlertTitle>
              <AlertDescription>
                Type <code className="px-1 py-0.5 bg-muted rounded">FORCE STOP</code> to confirm 
                this action.
              </AlertDescription>
            </Alert>
          </TabsContent>

          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="reason">Reason (optional)</Label>
              <Textarea
                id="reason"
                placeholder="Why are you cancelling this run?"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm">
                Type <code className="px-1 py-0.5 bg-muted rounded">{expectedConfirm}</code> to confirm
              </Label>
              <Input
                id="confirm"
                placeholder={expectedConfirm}
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
                autoComplete="off"
              />
              {/* Inline helper showing why button is disabled */}
              {!canConfirm && (
                <p className="text-xs text-muted-foreground">
                  Type <strong>{expectedConfirm}</strong> to enable the button
                </p>
              )}
              {canConfirm && cancelType === "FORCE_STOP" && staleSteps.length === 0 && (
                <p className="text-xs text-destructive">
                  No stale steps to force stop
                </p>
              )}
            </div>
          </div>
        </Tabs>

        <AlertDialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isCancelling}>
            Close
          </Button>
          <Button
            variant="destructive"
            onClick={handleCancel}
            disabled={!canSubmit}
          >
            {isCancelling ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {cancelType === "FORCE_STOP" ? "Force Stopping..." : "Cancelling..."}
              </>
            ) : cancelType === "FORCE_STOP" ? (
              "Force Stop"
            ) : (
              "Cancel Run"
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
